import type { SentimentBand } from '@playerbase/types';

// ---------------------------------------------------------------------------
// Overall-sentiment dot.
//
// A subtle coloured dot + label for a submission's or aggregate's derived
// sentiment band. Vocabulary and colours mirror the insights polarity dots
// (pattern-card.tsx) so the two surfaces read consistently: a positive band
// is teal, negative is danger, neutral is muted. Plain product copy only -
// the underlying derivation is never named in the UI.
//
// Renders nothing when band is null (no sentiment yet) so a card never shows
// an empty placeholder dot.
// ---------------------------------------------------------------------------

function bandDotClass(band: SentimentBand): string {
  if (band === 'positive') return 'bg-teal';
  if (band === 'negative') return 'bg-danger';
  return 'bg-text-muted';
}

function bandLabel(band: SentimentBand): string {
  if (band === 'positive') return 'Positive';
  if (band === 'negative') return 'Negative';
  return 'Neutral';
}

interface SentimentDotProps {
  band: SentimentBand | null;
  // When true, render the label text next to the dot. Defaults to a
  // dot-only chip for tight headers; the playtest detail card opts into
  // the label.
  showLabel?: boolean;
}

export function SentimentDot({
  band,
  showLabel = false,
}: SentimentDotProps): JSX.Element | null {
  if (band === null) return null;
  const label = bandLabel(band);
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        aria-label={label}
        title={label}
        className={`inline-block h-2 w-2 shrink-0 rounded-full ${bandDotClass(band)}`}
      />
      {showLabel && <span className="text-xs text-text-muted">{label}</span>}
    </span>
  );
}
